/**
 * SO-9a — the firm's strip over a party's Site Office, on EVERY page of the
 * preview. One component: the breadcrumb back into the desk, the two views,
 * who is being previewed, and the firm's own actions. The party never sees it.
 *
 * Props
 *   projectId, membershipId   the preview address (/projects/:pid/site-office/:mid)
 *   projectName, partyName    for the crumb
 *   page                      the sub-page name ("Submittals"); omitted on the front
 *   view                      'yours' | 'party'; onView(next) switches (front page only)
 *   line                      the "Previewing as …" sentence (front page only)
 *   children                  the firm's action buttons (front page only)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

// The preview address, carried on sub-page links as ?preview=<pid>.<mid>.
export const previewParam = (projectId, membershipId) => `${projectId}.${membershipId}`;

export const parsePreview = (value) => {
  const [projectId, membershipId] = String(value || '').split('.');
  return projectId && membershipId ? { projectId, membershipId } : null;
};

export const withPreview = (path, preview) =>
  preview ? `${path}${path.includes('?') ? '&' : '?'}preview=${previewParam(preview.projectId, preview.membershipId)}` : path;

export default function OfficeStrip({
  projectId, membershipId, projectName, partyName, page, view = 'party', onView, line, children,
}) {
  const navigate = useNavigate();
  const front = `/projects/${projectId}/site-office/${membershipId}${view === 'party' ? '?view=party' : ''}`;
  return (
    <div className="kso-admin" data-testid="site-office-admin-strip">
      <nav className="kso-admin__crumb" aria-label="Where you are" data-testid="site-office-crumb">
        <button type="button" onClick={() => navigate('/')}>My Desk</button>
        <button type="button" onClick={() => navigate(`/projects/${projectId}/desk`)}>{projectName || 'Project'}</button>
        <button type="button" onClick={() => navigate(`/projects/${projectId}/desk?tab=team&member=${membershipId}`)}>Team &amp; contacts</button>
        {page ? (
          <>
            <button type="button" onClick={() => navigate(front)}>{partyName}'s Site Office</button>
            <b>{page}</b>
          </>
        ) : <b>{partyName}'s Site Office</b>}
      </nav>
      {onView ? (
        <span className="kso-switch" data-testid="site-office-view-switch">
          <button type="button" aria-pressed={view !== 'party'} data-testid="site-office-view-yours"
            onClick={() => onView('yours')}>Your view</button>
          <button type="button" aria-pressed={view === 'party'} data-testid="site-office-view-theirs"
            onClick={() => onView('party')}>As they see it</button>
        </span>
      ) : null}
      {line ? <span data-testid="site-office-previewing">{line}</span> : null}
      <div className="kso-admin__sp">
        {children}
        <button type="button" className="kso-abtn" data-testid="site-office-back"
          onClick={() => navigate(`/projects/${projectId}/desk?tab=team&member=${membershipId}`)}>
          Back to the team
        </button>
      </div>
    </div>
  );
}
